/* eslint-disable sort-keys */
import { Box, Button,  Modal, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Clock, Download, MapPin, Monitor, Trash2, Upload, X } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { getStatusBadge } from "./courseDetailUtils";
import { DomainBox } from "../../components/SignupDomain/SignupDomain.styles";

interface CourseDetailModalProps {
  open: boolean;
  course: CourseItem;
  onClose: () => void;
  onDownloadCertificate: (url: string) => void;
  onRequestUploadCertificate: (course: CourseItem) => void;
  onEdit: (course: CourseItem) => void;
  onDelete?: (course: CourseItem) => void;
  onGoToExplore?: (course: CourseItem) => void;
}

export const CourseDetailModal: React.FC<CourseDetailModalProps> = ({
  open,
  course,
  onClose,
  onDownloadCertificate,
  onRequestUploadCertificate,
  onEdit,
  onDelete,
  onGoToExplore
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isInternal = course.source === "internal";

  const formatDateIt = (dateString?: string) => {
    return dateString
      ? new Date(dateString).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
      : "-";
  };

  const formatDuration = (minutes?: number) => {
    if (!minutes) return "-";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) return `${hours}h${mins}m`;
    if (hours > 0) return `${hours}h`;
    return `${mins}m`;
  };

  const getSubtitle = () => {
    if (isInternal) {
      return `ID Open Learning | Codice: ${course.id}`;
    }
    return `ID | Codice: ${course.id}`;
  };

  const getTrainingType = () => {
    if (isInternal) return "Interna";
    return `Esterna (${course.type?.toLowerCase() ?? ""})`;
  };

  const getFruitionIcon = () => {
    if (course.trainingMode?.toLowerCase().includes("online") ||
        course.trainingMode?.toLowerCase().includes("e-learning")) {
      return <Monitor size={ 16 } />;
    }
    return <MapPin size={ 16 } />;
  };

  const getFruitionLabel = () => {
    return course.trainingMode || "-";
  };

  const knowledgeChips = course.area?.flatMap(a => [
    { isArea: true, label: a.label },
    ...(a.domain || []).map((d: { label: string }) => ({ isArea: false, label: d.label }))
  ]) ?? [];

  const canUpload = !course.pdfKey &&
    course.status !== "PENDING" &&
    course.status !== "REJECTED" &&
    !(course.type === "Consuntivazione" && course.status === "APPROVED");

  // TODO: aggiungere la logica per gli altri stati quando ci saranno
  const canEdit =
    course.status !== "PENDING" &&
    course.status !== "REJECTED" &&
    !(course.type === "Consuntivazione" && course.status === "APPROVED");

  const canDelete = !isInternal && course.status === "PENDING" && Boolean(onDelete);

  const showActionButtonRow = isInternal
    ? Boolean(course.pdfKey || onGoToExplore)
    : Boolean(course.pdfKey || canUpload || canEdit || canDelete);

  return (
    <Modal
      open={ open }
      onClose={ onClose }
      sx={ { zIndex: 1400 } }
    >
      <Box
        sx={ {
          bgcolor: theme.customColors.backgroundPrimary,
          border: `2px solid ${theme.customColors.border}`,
          borderRadius: "16px",
          left: "50%",
          maxHeight: "90vh",
          maxWidth: 900,
          outline: "none",
          overflowY: "auto",
          p: 4,
          position: "absolute",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "95vw" : "80vw",
          display: "flex",
          flexDirection: "column",
          gap: "2.5rem"

        } }
      >
        { /* Close button */ }
        <Box
          onClick={ onClose }
          sx={ {
            cursor: "pointer",
            position: "absolute",
            right: "1.5rem",
            top: "1.5rem"
          } }
        >
          <X size={ 24 } color={ theme.customColors.textPrimary } />
        </Box>
        <Box>
          { /* Title */ }
          <Typography
            variant="h5"
            fontWeight={ 700 }
            color={ theme.customColors.textPrimary }
            sx={ { pr: 4 } }
          >
            { course.title }
          </Typography>

          { /* Subtitle */ }
          <Typography
            variant="body2"
            color={ theme.customColors.textPrimary }
            sx={ { mt: 0.5, opacity: 0.7 } }
          >
            { getSubtitle() }
          </Typography>

          { /* Info grid */ }
          <Box
            sx={ {
              display: "grid",
              gap: 3,
              gridTemplateColumns: isMobile
                ? "1fr 1fr"
                : "1.2fr 1.2fr 1fr 1fr 1fr 1fr",
              mt: "2.5rem"
            } }
          >
            { /* Stato */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("status") }
              </Typography>
              <Box sx={ { mt: 0.5 } }>
                { getStatusBadge(course.status, theme, t, {
                  height: "auto",
                  minHeight: "1.5rem",
                  alignItems: "flex-start",
                  "& > svg": { flexShrink: 0, marginTop: "0.15rem" },
                  "& > span": { whiteSpace: "normal" }
                }) }
              </Box>
            </Box>

            { /* Tipo di formazione */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("type_training") }
              </Typography>
              <Typography
                variant="body2"
                fontWeight={ 600 }
                color={ theme.customColors.textPrimary }
                sx={ { mt: 0.5 } }
              >
                { getTrainingType() }
              </Typography>
            </Box>

            { /* Data di inizio */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("date_start_training") }
              </Typography>
              <Typography
                variant="body2"
                fontWeight={ 600 }
                color={ theme.customColors.textPrimary }
                sx={ { mt: 0.5 } }
              >
                { formatDateIt(course.startDate) }
              </Typography>
            </Box>

            { /* Data di fine */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("date_end_training") }
              </Typography>
              <Typography
                variant="body2"
                fontWeight={ 600 }
                color={ theme.customColors.textPrimary }
                sx={ { mt: 0.5 } }
              >
                { formatDateIt(course.endDate) }
              </Typography>
            </Box>

            { /* Fruizione */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("fruition") || "Fruizione" }
              </Typography>
              <Box
                sx={ {
                  alignItems: "center",
                  color: theme.customColors.textPrimary,
                  display: "flex",
                  gap: 0.5,
                  mt: 0.5
                } }
              >
                { getFruitionIcon() }
                <Typography variant="body2" fontWeight={ 600 }>
                  { getFruitionLabel() }
                </Typography>
              </Box>
            </Box>

            { /* Durata */ }
            <Box>
              <Typography
                variant="caption"
                color={ theme.customColors.textPrimary }
                sx={ { opacity: 0.6 } }
              >
                { t("duration") || "Durata" }
              </Typography>
              <Box
                sx={ {
                  alignItems: "center",
                  color: theme.customColors.textPrimary,
                  display: "flex",
                  gap: 0.5,
                  mt: 0.5
                } }
              >
                <Clock size={ 16 } style={ { flexShrink: 0 } } />
                <Typography variant="body2" fontWeight={ 600 } sx={ { whiteSpace: "nowrap" } }>
                  { formatDuration(course.duration) }
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        { (course.description || knowledgeChips.length > 0) && (
          <Box
            display="flex"
            flexDirection="column"
            gap={ "1.5rem" }
          >
            { /* Description */ }
            { course.description && (
              <Typography
                variant="body2"
                color={ theme.customColors.textPrimary }
                sx={ { lineHeight: 1.6 } }
              >
                { course.description }
              </Typography>
            ) }

            { /* Area & Domain chips */ }
            { knowledgeChips.length > 0 && (
              <Box sx={ { display: "flex", flexWrap: "wrap", gap: 1 } }>
                { knowledgeChips.map((chip, idx) => (
                  <DomainBox
                    key={ `${chip.label}-${idx}` }
                    sx={ {
                      alignItems: "center",
                      backgroundColor: chip.isArea
                        ? theme.customColors.backgroundPrimaryCta
                        : `rgba(${theme.customColors.backgroundSecondaryHover})`,
                      height: "1.5rem",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "0.25rem",
                      padding: "0.44rem 0.5rem",
                      boxShadow: "none",
                      cursor: "default",
                      "&:hover": {
                        boxShadow: "none"
                      }
                    } }
                  >
                    <Typography
                      fontSize={ "0.75rem" }
                      fontWeight={ 600 }
                      color={ chip.isArea
                        ? theme.customColors.textWidgetAlternative
                        : theme.customColors.textPrimary }
                    >
                      { chip.label }
                    </Typography>
                  </DomainBox>
                )) }
              </Box>
            ) }
          </Box>
        ) }
        { showActionButtonRow && (
          <Box>
            { /* Action buttons */ }
            <Box
              sx={ {
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                justifyContent: "flex-end"
              } }
            >
              { isInternal ? (
                <>
                  { course.pdfKey && (
                    <Button
                      onClick={ () => onDownloadCertificate(course.pdfKey as string) }
                      sx={ {
                        backgroundColor: theme.customColors.backgroundPrimaryCta,
                        borderRadius: "8px",
                        color: theme.customColors.textWidgetAlternative,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: theme.customColors.backgroundPrimaryCta,
                          opacity: 0.9
                        }
                      } }
                      startIcon={ <Download size={ 18 } /> }
                    >
                      { t("download_certificate_button") }
                    </Button>
                  ) }
                  { onGoToExplore && (
                    <Button
                      onClick={ () => onGoToExplore(course) }
                      variant="outlined"
                      sx={ {
                        borderColor: theme.customColors.border,
                        borderRadius: "8px",
                        color: theme.customColors.textPrimary,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        textTransform: "none"
                      } }
                    >
                      { t("go_to_explore") || "Vai al corso in Esplora" }
                      { " >" }
                    </Button>
                  ) }
                </>
              ) : (
                <>
                  { course.pdfKey ? (
                    <Button
                      onClick={ () => onDownloadCertificate(course.pdfKey as string) }
                      sx={ {
                        backgroundColor: theme.customColors.backgroundPrimaryCta,
                        borderRadius: "8px",
                        color: theme.customColors.textWidgetAlternative,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        textTransform: "none",
                        "&:hover": {
                          backgroundColor: theme.customColors.backgroundPrimaryCta,
                          opacity: 0.9
                        }
                      } }
                      startIcon={ <Download size={ 18 } /> }
                    >
                      { t("download_certificate") }
                    </Button>
                  ) : canUpload ? (
                    <Button
                      onClick={ () => onRequestUploadCertificate(course) }
                      sx={ {
                        //backgroundColor: theme.customColors.backgroundPrimaryCta,
                        borderRadius: "8px",
                        color: theme.customColors.backgroundPrimaryCta,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        textTransform: "none"

                      } }
                      startIcon={ <Upload color={ theme.customColors.backgroundPrimaryCta } size={ 18 } /> }
                    >
                      { t("file_field") || "Carica il certificato" }
                    </Button>
                  ) : null }
                  { canEdit ? (
                    <Button
                      onClick={ () => onEdit(course) }
                      variant="outlined"
                      sx={ {
                        borderColor: theme.customColors.border,
                        borderRadius: "8px",
                        color: theme.customColors.textPrimary,
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        px: 3,
                        py: 1.2,
                        textTransform: "none"
                      } }
                    >
                      { t("edit") || "Modifica" }
                    </Button>
                  ) : null }
                  { canDelete && onDelete ? (
                    <Button
                      onClick={ () => onDelete(course) }
                      variant="outlined"
                      aria-label={ t("delete") }
                      sx={ {
                        borderColor: theme.customColors.border,
                        borderRadius: "8px",
                        color: theme.customColors.textPrimary,
                        minWidth: "unset",
                        px: 2,
                        py: 1.2
                      } }
                    >
                      <Trash2 size={ 18 } />
                    </Button>
                  ) : null }
                </>
              ) }
            </Box>
          </Box>

        ) }
      </Box>
    </Modal>
  );
};
